import { createFileRoute } from "@tanstack/react-router";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { getLifecycleStatuses } from "@/utils/lifecycle-detail/lifecycle-detail.utils";
import { useLifecycleDrawerContext } from "@/utils/lifecycle-detail/lifecycle-drawer-context";
import { resolveStatusCategoryColors } from "@/utils/status-category-colors";

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/lifecycles/$lifecycleId/statuses",
)({
	component: LifecycleStatusesTabContent,
});

function LifecycleStatusesTabContent() {
	const { t } = useAppTranslation();
	const { lifecycle } = useLifecycleDrawerContext();
	const statuses = getLifecycleStatuses(lifecycle);

	return (
		<div className="space-y-4 px-5 pb-5">
			<div>
				<h3 className="text-[16px] font-semibold text-primary">
					{t("admin.lifecycles.drawer.statuses.title")}
				</h3>
				<p className="mt-0.5 text-sm text-gray-500">
					{t("admin.lifecycles.drawer.statuses.description")}
				</p>
			</div>

			<div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
				{statuses.length === 0 ? (
					<p className="py-8 text-center text-sm text-gray-500">
						{t("admin.lifecycles.drawer.statuses.empty")}
					</p>
				) : (
					<div className="space-y-0">
						{statuses.map((status, index) => {
							const iconColor =
								(((status as Record<string, unknown>).statusCategoryIconColor as
									| string
									| undefined) ??
								(status as Record<string, unknown>).statusCategory)
									? ((
											(status as Record<string, unknown>)
												.statusCategory as Record<string, unknown>
										)?.iconColor as string | undefined)
									: undefined;
							const { color, bgColor } = resolveStatusCategoryColors(iconColor);

							const categoryName =
								(((status as Record<string, unknown>).statusCategoryName as
									| string
									| undefined) ??
								(status as Record<string, unknown>).statusCategory)
									? ((
											(status as Record<string, unknown>)
												.statusCategory as Record<string, unknown>
										)?.name as string | undefined)
									: undefined;

							const isLast = index === statuses.length - 1;

							return (
								<div key={status.id} className="flex gap-3">
									{/* Timeline */}
									<div className="flex shrink-0 flex-col items-center">
										<div
											className="flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-semibold text-gray-900"
											style={{ backgroundColor: bgColor, borderColor: color }}
										>
											{index + 1}
										</div>
										{!isLast && (
											<div className="mt-1 min-h-3 w-0.5 flex-1 bg-gray-200" />
										)}
									</div>

									{/* Card */}
									<div className="mb-3 flex flex-1 flex-col rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
										<div className="flex items-center gap-2">
											<span className="font-semibold text-gray-900">
												{status.name}
											</span>
											{categoryName && (
												<span
													className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium"
													style={{
														backgroundColor: bgColor,
														borderColor: color,
														color,
													}}
												>
													{categoryName.toUpperCase()}
												</span>
											)}
										</div>
										{status.description && (
											<p className="mt-1 text-sm leading-relaxed text-gray-600">
												{status.description}
											</p>
										)}
									</div>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}
